import _ from 'lodash';
import md5 from 'blueimp-md5';
import io from 'socket.io-client';
import { history, matchPath } from 'umi';
import tokenEvents, { TOKEN_EVENTS } from './tokenEvents';

class SocketService {
  constructor() {
    if (SocketService._instance) {
      return SocketService._instance;
    }

    this._initializeProperties();
    this._setupTokenEventListeners();
    this._setupLifecycleListeners();
    this._startJoiningGroupsCleanup();
    SocketService._instance = this;
  }

  static getInstance() {
    if (!SocketService._instance) {
      SocketService._instance = new SocketService();
    }
    return SocketService._instance;
  }

  get connected() {
    return this.isConnected;
  }

  get token() {
    return this._getTokenFromOptions();
  }

  _initializeProperties() {
    this.socket = null;
    this.currentGroup = null;
    this.isConnected = false;
    this.messageQueue = new Map();
    this.joiningGroups = new Map();
    this.eventListeners = new Map();
    this.initialized = false;
    this.serverUrl = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.isReconnecting = false;
    this.reconnectDelay = 2000;
    this.joiningGroupsCleanupInterval = null;
    this.joiningGroupsCleanupIntervalMs = 5000;
    this.joiningGroupsInFlightTtl = 15000;
  }

  /**
   * Setup listeners for token refresh events
   */
  _setupTokenEventListeners() {
    // Listen for token refresh events
    tokenEvents.on(TOKEN_EVENTS.TOKEN_REFRESHED, this._handleTokenRefresh.bind(this));
    tokenEvents.on(TOKEN_EVENTS.SOCKET_RECONNECT_NEEDED, this._handleReconnectRequest.bind(this));
  }

  /**
   * Setup page lifecycle listeners to proactively restore socket connection
   * after the app returns to foreground or network becomes online
   */
  _setupLifecycleListeners() {
    try {
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          try {
            if (
              document.visibilityState === 'visible' &&
              (!this.socket || !this.socket.connected) &&
              this.serverUrl
            ) {
              this._attemptReconnectWithToken(this.token).catch(() => {});
            }
          } catch (e) {}
        });
      }

      if (typeof window !== 'undefined') {
        window.addEventListener('online', () => {
          try {
            if ((!this.socket || !this.socket.connected) && this.serverUrl) {
              this._attemptReconnectWithToken(this.token).catch(() => {});
            }
          } catch (e) {}
        });
      }
    } catch (e) {}
  }

  _startJoiningGroupsCleanup() {
    if (this.joiningGroupsCleanupInterval) return;
    this.joiningGroupsCleanupInterval = setInterval(() => {
      try {
        this._cleanupJoiningGroups();
      } catch (e) {
        // Silent fail
      }
    }, this.joiningGroupsCleanupIntervalMs);
  }

  _cleanupJoiningGroups() {
    const now = Date.now();
    this.joiningGroups.forEach((entry, key) => {
      const { joining, disconnecting, timestamp } = entry || {};
      if (
        (joining || disconnecting) &&
        timestamp &&
        now - timestamp > this.joiningGroupsInFlightTtl
      ) {
        this.joiningGroups.delete(key);
      }
    });
  }

  _normalizeJoinKey(key) {
    if (!key && key !== 0) return null;
    return key.toString().toUpperCase();
  }

  _markJoiningGroupEntry(key, entry) {
    const normalizedKey = this._normalizeJoinKey(key);
    if (!normalizedKey) return;
    const existing = this.joiningGroups.get(normalizedKey) || {};
    this.joiningGroups.set(normalizedKey, {
      ...existing,
      ...entry,
      data: entry?.data || existing.data,
      timestamp: Date.now(),
    });
  }

  _releaseJoiningGroupEntry(key) {
    const normalizedKey = this._normalizeJoinKey(key);
    if (!normalizedKey) return;
    this.joiningGroups.delete(normalizedKey);
  }

  _isJoinEvent(event, data) {
    return (
      event === 'joinChat' ||
      event === 'joinGroup' ||
      data?.type === 'joinChat' ||
      data?.type === 'joinGroup'
    );
  }

  _isLeaveEvent(event, data) {
    return event === 'leaveGroup' || data?.type === 'leaveChat' || data?.type === 'leaveGroup';
  }

  /**
   * Handle token refresh event
   * @param {Object} data - Token refresh data
   */
  async _handleTokenRefresh(data) {
    const { token, userId } = data;

    if (!this.socket || !this.serverUrl) return;

    // Update socket auth with new token
    if (this.socket.auth) {
      this.socket.auth.token = token;
    }

    // If socket is disconnected, try to reconnect with new token
    if (!this.isConnected) {
      await this._attemptReconnectWithToken(token);
    }
  }

  /**
   * Handle reconnection request
   * @param {Object} data - Reconnection request data
   */
  async _handleReconnectRequest(data) {
    const { reason } = data;

    if (!this.socket || !this.serverUrl) return;

    // Force reconnection with current token
    await this._attemptReconnectWithToken(this.token);
  }

  /**
   * Attempt to reconnect with new token
   * @param {string} newToken - New access token
   */
  async _attemptReconnectWithToken(newToken) {
    if (this.isReconnecting) return;

    this.isReconnecting = true;
    this.reconnectAttempts = 0;

    try {
      await this._performReconnection(newToken);

      // Emit success event
      tokenEvents.emit(TOKEN_EVENTS.SOCKET_RECONNECTED, {
        timestamp: Date.now(),
        attempts: this.reconnectAttempts + 1,
      });
    } catch (error) {
      // Emit failure event
      tokenEvents.emit(TOKEN_EVENTS.SOCKET_RECONNECT_FAILED, {
        error,
        attempts: this.reconnectAttempts + 1,
        timestamp: Date.now(),
      });
    } finally {
      this.isReconnecting = false;
    }
  }

  /**
   * Perform the actual reconnection with retry logic
   * @param {string} newToken - New access token
   */
  async _performReconnection(newToken) {
    while (this.reconnectAttempts < this.maxReconnectAttempts) {
      try {
        // Disconnect current socket
        if (this.socket) {
          this.socket.disconnect();
        }

        // Wait a bit before reconnecting
        if (this.reconnectAttempts > 0) {
          await new Promise((resolve) =>
            setTimeout(resolve, this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1)),
          );
        }

        // Attempt reconnection
        this.socket = io(this.serverUrl, {
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          auth: { token: newToken },
          transports: ['websocket'],
          path: '/ws',
        });

        // Setup events for new socket
        // Reset initialized flag to configure listeners for the new socket instance
        this.initialized = false;
        this._setupSocketEvents();
        this._startJoiningGroupsCleanup();
        // Rebind any external listeners registered via SocketService.on(...)
        this._rebindEventListeners();

        // Wait for connection
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Connection timeout'));
          }, 10000);

          this.socket.once('connect', () => {
            clearTimeout(timeout);
            resolve();
          });

          this.socket.once('connect_error', (error) => {
            clearTimeout(timeout);
            reject(error);
          });
        });

        // Success - break out of retry loop
        this.reconnectAttempts = 0;
        return;
      } catch (error) {
        this.reconnectAttempts++;

        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          throw new Error(
            `Failed to reconnect after ${this.maxReconnectAttempts} attempts: ${error.message}`,
          );
        }
      }
    }
  }

  _getTokenFromOptions() {
    let tokenStoredKey = 'token';

    const pathname = _.get(history.location, 'pathname');
    const externalUrlMatch = matchPath(pathname, { path: '/external/', exact: false });

    if (externalUrlMatch) {
      const { publicCode, code } = history.location.query || {};
      tokenStoredKey = publicCode ? `publicToken"${publicCode}"` : `externalToken"${md5(code)}"`;
    }

    try {
      const storedToken = localStorage.getItem(`x-auth-${tokenStoredKey}`);
      return JSON.parse(storedToken)?.accessToken || null;
    } catch (e) {
      return null;
    }
  }

  /**
   *
   * @param {String} serverUrl
   * @param {[String]} uToken
   */
  init(serverUrl, uToken = null) {
    // Store server URL for reconnections
    this.serverUrl = serverUrl;

    // Si ya hay un socket conectado con la misma URL, no crear otro
    if (this.socket && this.socket.connected && this.socket.io.uri === serverUrl) {
      return;
    }

    // If there's a socket but not connected or different URL, disconnect first
    if (this.socket) {
      this.disconnect();
    }

    const token = uToken || this._getTokenFromOptions();

    this.socket = io(serverUrl, {
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      auth: { token },
      transports: ['websocket'],
      path: '/ws',
    });

    this._setupSocketEvents();
    this._startJoiningGroupsCleanup();
  }

  _setupSocketEvents() {
    // Verify socket exists and is not already configured
    if (!this.socket || this.initialized) return;

    this.initialized = true;

    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      this._processQueue();
      // Restore previously joined groups/rooms immediately
      this._restoreJoins();
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      try {
        if (typeof window !== 'undefined') window.__supportChatCanSend = false;
      } catch (e) {}
      // Keep joins state; upon reconnect, _restoreJoins will re-emit
    });

    this.socket.on('reconnect', (attempt) => {
      this.isConnected = true;
      this.reconnectAttempts = 0; // Reset reconnect attempts
      this._processQueue();
      // Restore previously joined groups/rooms immediately
      this._restoreJoins();
    });

    this.socket.on('connect_error', (err) => {
      this.isConnected = false;

      // Check if it's an authentication error (401)
      if (
        err?.message === 'Authentication error' ||
        err?.data?.statusCode === 401 ||
        err?.type === 'AuthenticationError' ||
        (err?.message && err.message.includes('401'))
      ) {
        // Emit token event for authentication errors
        tokenEvents.emit(TOKEN_EVENTS.SOCKET_AUTH_ERROR, {
          error: err,
          timestamp: Date.now(),
          socketService: this,
        });

        // Also dispatch custom event for backward compatibility
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('socketAuthError', {
              detail: { error: err, socketService: this },
            }),
          );
        }
      }
      // If network is back later or visibility changes, lifecycle listeners will trigger reconnection
    });

    // Global listener to detect invalid_token messages from any channel
    // and trigger the authentication error flow
    if (typeof this.socket.onAny === 'function') {
      this.socket.onAny((event, data) => {
        try {
          if (data && typeof data === 'object' && data.message === 'invalid_token') {
            tokenEvents.emit(TOKEN_EVENTS.SOCKET_AUTH_ERROR, {
              error: { message: 'invalid_token', event },
              timestamp: Date.now(),
              socketService: this,
            });

            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('socketAuthError', {
                  detail: { error: { message: 'invalid_token', event }, socketService: this },
                }),
              );
            }
          }
        } catch (e) {
          // Silent fail
        }
      });
    }
  }

  /**
   * Rebind previously registered external event listeners to the current socket instance
   */
  _rebindEventListeners() {
    if (!this.socket || !this.eventListeners || this.eventListeners.size === 0) return;
    this.eventListeners.forEach((callback, event) => {
      try {
        this.socket.on(event, callback);
      } catch (e) {}
    });
  }

  /**
   * Re-emit join messages for previously joined groups/rooms after reconnection
   */
  _restoreJoins() {
    if (!this.socket) return;
    try {
      this.joiningGroups.forEach((entry, key) => {
        const payload = entry?.data;
        // Force event to be a join operation; never replay chatMessage or others
        const eventName = payload?.threadId ? 'joinChat' : 'joinGroup';
        if (payload) {
          this._emitMessage(eventName, payload, null, null);
        }
      });
    } catch (e) {}
  }

  send(event, data, onSuccess, onError) {
    if (!data.groupId && !data.threadId) return;

    const key = data.groupId || data.threadId;
    const normalizedKey = this._normalizeJoinKey(key);
    const isJoinEvent = this._isJoinEvent(event, data);
    const joinEntry = normalizedKey ? this.joiningGroups.get(normalizedKey) : null;

    if (isJoinEvent && normalizedKey && this.joiningGroups.has(normalizedKey)) return;
    if (isJoinEvent && joinEntry?.joining) return;

    if (this.isConnected) {
      if (isJoinEvent) {
        this._markJoiningGroupEntry(normalizedKey || key, {
          data,
          joining: true,
          disconnecting: false,
        });
      }
      this._emitMessage(event, data, onSuccess, onError);
    } else if (!this.messageQueue.has(key)) {
      this.messageQueue.set(key, { event, data, onSuccess, onError });
    }
  }

  leaveGroup(onSuccess = null, onError = null) {
    if (!(this.currentGroup && this.isConnected)) return;

    const key = this.currentGroup.groupId;
    if (this.joiningGroups.get(key)?.disconnecting) return;

    this._markJoiningGroupEntry(key, {
      data: this.currentGroup,
      disconnecting: true,
      joining: false,
    });
    this._leaveCurrentGroup(this.currentGroup, onSuccess, onError);
  }

  leaveChat(threadId, onSuccess = null, onError = null) {
    if (!this.isConnected || !threadId) return;

    const key = threadId.toString().toUpperCase();
    if (this.joiningGroups.get(key)?.disconnecting) return;

    const chatData = {
      type: 'leaveChat',
      threadId: key,
      groupId: key,
    };

    this._markJoiningGroupEntry(key, {
      data: chatData,
      disconnecting: true,
      joining: false,
    });
    this._leaveCurrentGroup(chatData, onSuccess, onError);
  }

  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
      this.eventListeners.set(event, callback);
    }
  }

  off(event) {
    if (this.socket && this.eventListeners.has(event)) {
      this.socket.off(event, this.eventListeners.get(event));
      this.eventListeners.delete(event);
    }
  }

  _emitMessage(event, data, onSuccess, onError) {
    // If socket not connected
    if (!this.socket || !this.socket.connected) {
      // DO NOT queue chat messages when offline to avoid confusing user
      if (event === 'chatMessage') {
        if (onError) onError({ message: 'offline' });
        return;
      }
      // For other events (joins, etc.) keep minimal queuing
      const key = data.groupId || data.threadId;
      if (key && !this.messageQueue.has(key)) {
        this.messageQueue.set(key, { event, data, onSuccess, onError });
      }
      return;
    }

    this.socket.emit(event, data, (ok) => {
      if (ok) {
        this._handleMessageAck(event, data, onSuccess);
      } else {
        this._handleMessageError(event, data);
        if (onError) onError(data);
      }
    });
  }

  _handleMessageAck(event, data, onSuccess) {
    const isJoinEvent = this._isJoinEvent(event, data);

    if (isJoinEvent) {
      if (data.type !== 'all') this.currentGroup = data;
      // Persist ONLY join events to restore after reconnection
      this._markJoiningGroupEntry(data.groupId, {
        data,
        event: data?.type || event,
        joining: false,
        disconnecting: false,
      });
      // Notify UI when joins are confirmed so input can be enabled immediately
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('socketJoined', {
              detail: {
                event: event || data?.type,
                groupId: data?.groupId,
                threadId: data?.threadId,
              },
            }),
          );
        }
      } catch (e) {}
    }

    if (onSuccess) onSuccess(data);
  }

  _handleMessageError(event, data) {
    if (this._isJoinEvent(event, data) || this._isLeaveEvent(event, data)) {
      const key = data?.groupId || data?.threadId;
      this._releaseJoiningGroupEntry(key);
    }
  }

  _leaveCurrentGroup(data, onSuccess, onError) {
    if (!this.socket) return;

    const eventName = data?.type === 'leaveChat' ? 'leaveChat' : 'leaveGroup';

    this.socket.emit(eventName, data, (ok) => {
      if (ok) {
        this.off(data.type);
        this._handleLeaveGroupAck(data, onSuccess);
      } else {
        this._handleMessageError(eventName, data);
        if (onError) onError(data);
      }
    });
  }

  _handleLeaveGroupAck(data, onSuccess) {
    this.currentGroup = null;
    this._releaseJoiningGroupEntry(data.groupId);
    if (onSuccess) onSuccess(data);
  }

  _processQueue() {
    for (const [key, { event, data, onSuccess, onError }] of this.messageQueue) {
      // Skip chat messages to ensure no offline queue for user messages
      if (event === 'chatMessage') {
        this.messageQueue.delete(key);
        continue;
      }
      this._emitMessage(event, data, onSuccess, onError);
      this.messageQueue.delete(key);
    }
  }

  _offAll() {
    if (this.socket) {
      this.eventListeners.forEach((callback, event) => {
        this.socket.off(event, callback);
      });
      this.eventListeners.clear();
    }
  }

  _leaveAll() {
    if (this.socket) {
      this.joiningGroups.forEach(({ data }) => {
        this._leaveCurrentGroup(data, null, null);
      });
      this.joiningGroups.clear();
    }
  }

  getSocket() {
    return this.socket;
  }

  // Method to verify if socket is properly initialized
  isInitialized() {
    return this.socket && this.initialized;
  }

  disconnect() {
    if (!this.socket) return;

    this._offAll();
    this._leaveAll();
    this.messageQueue.clear();
    this.socket.disconnect();
    this.isConnected = false;
    this.currentGroup = null;
    this.socket = null;
    // Reset initialized flag to allow new configuration
    this.initialized = false;
    this.serverUrl = null;
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
    if (this.joiningGroupsCleanupInterval) {
      clearInterval(this.joiningGroupsCleanupInterval);
      this.joiningGroupsCleanupInterval = null;
    }
  }

  // Method to reconnect with new token
  reconnectWithNewToken(serverUrl, newToken = null) {
    // Completely disconnect and reconnect
    this.disconnect();
    this.init(serverUrl, newToken);
  }
}

export default SocketService;
